import { Alert, Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { IRecordFurtherEducationGroupNode } from "ui/Scripts/source/models/recordFurtherEducationGroup/recordFurtherEducationGroupNode";
import { RecordListItemUserQualification } from "ui/Scripts/source/models/recordUserQualification/recordListItemUserQualification";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { IRecordEditItemFurtherEducation, RecordEditItemFurtherEducation } from "../../../models/record/recordEditItemFurtherEducation";
import { IRecordEditItemQualification } from "../../../models/record/recordEditItemQualification";
import * as recordService from "../../../models/services/recordService";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { IListValueLineData } from "../../framework/List/ListLine";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { ListStateIconCertificate } from "../../framework/List/ListStateIconCertificate";
import { RecordEditListFirstCol } from "./RecordEditListFirstCol";
import { RecordUserFurtherEducationDialog } from "../RecordUserFurtherEducationDialog";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

interface IListFurtherEducationGroup {
    id: string;
    code: string;
    qualification: IRecordEditItemQualification;
    educations: IRecordEditItemFurtherEducation[];
}

export interface IRecordEditFurtherEducationListProp {
    isReadOnly: boolean;
    companyId: string;
    recordId: string;
    userCode: string;
    recordQualifications: IRecordEditItemQualification[];
    recordFurtherEducations: IRecordEditItemFurtherEducation[];
    furtherEducationGroups: IRecordFurtherEducationGroupNode[];
    attachments: IFileAttachment[];
    onRemoved: (ids: string[]) => void;
    onSaved: (recordFurtherEducation: IRecordEditItemFurtherEducation) => void;
    onAttachmentsRemoved: (ids: string[]) => void;
    onAttachmentsAdded: (attachments: IFileAttachment[]) => void;
}

interface IRecordEditFurtherEducationListState {
    // List
    isLoading: boolean;
    furtherEducationGroups: IListFurtherEducationGroup[];
    items: IRecordEditItemFurtherEducation[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];
    showArchived: boolean;
    selectedItemHasPrevious: boolean;
    selectedItemHasNext: boolean;

    // Dialog
    item: IRecordEditItemFurtherEducation;
    showEditDialog: boolean;
}

export class RecordEditFurtherEducationList extends React.Component<IRecordEditFurtherEducationListProp, IRecordEditFurtherEducationListState> {
    getFilteredList = (filter: string, showArchived: boolean, column: string, asc: boolean): IRecordEditItemFurtherEducation[] => {
        const props = this.props;
        let result = props.recordFurtherEducations ? props.recordFurtherEducations.slice(0) : [];
        RecordEditItemFurtherEducation.setFurtherEducationCodes(result, props.furtherEducationGroups);
        result = result.filter(i => showArchived ? true : i.activeState > 0.5);
        if (filter) {
            const lowFilter = filter.toLowerCase();
            result = result.filter(i => i.furtherEducationCode.toLowerCase().indexOf(lowFilter) > -1 || i.comment.toLowerCase().indexOf(lowFilter) > -1);
        }
        RecordEditItemFurtherEducation.sortRecordEditItemFurtherEducations(result, column, asc);
        return result;
    };

    getListFurtherEducationGroups = (recordFurtherEducations: IRecordEditItemFurtherEducation[]): IListFurtherEducationGroup[] => {
        const props = this.props;
        const result: IListFurtherEducationGroup[] = [];
        for (const furtherEducationGroup of props.furtherEducationGroups) {
            for (let i = 0; i < recordFurtherEducations.length; i++) {
                const item = recordFurtherEducations[i];
                if (furtherEducationGroup.children.find(i => i.id === item.furtherEducationId)) {
                    let resultGroup = result.find(j => j.id === furtherEducationGroup.id);
                    if (!resultGroup) {
                        resultGroup = {
                            id: furtherEducationGroup.id,
                            code: furtherEducationGroup.code,
                            qualification: null,
                            educations: []
                        };
                        if (furtherEducationGroup.requiredQualificationCount > 0) {
                            resultGroup.qualification = props.recordQualifications.find(i => i.furtherEducationGroupId === resultGroup.id);
                        }
                        result.push(resultGroup);
                    }
                    resultGroup.educations.unshift(item);
                }
            }
        }
        return result;
    };

    getFurtherEducationsSortedByGroups = (furtherEducationGroups: IListFurtherEducationGroup[]): IRecordEditItemFurtherEducation[] => {
        let result: IRecordEditItemFurtherEducation[] = [];
        for (const furtherEducationGroup of furtherEducationGroups) {
            result = result.concat(furtherEducationGroup.educations);
        }
        return result;
    };

    constructor(props: IRecordEditFurtherEducationListProp) {
        super(props);
        const recordFurtherEducations = this.getFilteredList("", false, "startDate", false);
        const furtherEducationGroups = this.getListFurtherEducationGroups(recordFurtherEducations);
        this.state = {
            isLoading: false,
            items: this.getFurtherEducationsSortedByGroups(furtherEducationGroups),
            furtherEducationGroups,
            filter: "",
            sortColumn: "startDate",
            sortOrderIsAsc: false,
            selectedId: null,
            checkedIds: [],
            showArchived: false,
            selectedItemHasPrevious: false,
            selectedItemHasNext: false,
            item: null,
            showEditDialog: false,
        };
    }

    filteredList = (filter: string, showArchived: boolean, column: string, asc: boolean) => {
        const recordFurtherEducations = this.getFilteredList(filter, showArchived, column, asc);
        const furtherEducationGroups = this.getListFurtherEducationGroups(recordFurtherEducations);
        const items = this.getFurtherEducationsSortedByGroups(furtherEducationGroups);
        const item = this.state.showEditDialog && this.state.item
            ? items.find(i => i.id === this.state.item.id)
            : null;
        const index = item ? items.findIndex(i => i.id === item.id) : -1;
        this.setState({
            filter,
            showArchived,
            sortColumn: column,
            sortOrderIsAsc: asc,
            items,
            item,
            showEditDialog: !!item,
            furtherEducationGroups,
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < items.length - 1,
        });
    };

    componentDidUpdate(prevProps: IRecordEditFurtherEducationListProp, prevState: IRecordEditFurtherEducationListState) {
        const props = this.props;
        const state = this.state;
        if (EditItem.getHash(prevProps.recordFurtherEducations) === EditItem.getHash(props.recordFurtherEducations)) return;
        this.filteredList(state.filter, state.showArchived, state.sortColumn, state.sortOrderIsAsc);
    };

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.showArchived, state.sortColumn, state.sortOrderIsAsc);
    };

    changeShowArchived = (showArchived: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, showArchived, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, state.showArchived, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: IRecordEditFurtherEducationListProp, recordFurtherEducationIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("ids", recordFurtherEducationIds);
        return result;
    };

    handleRemove = async () => {
        const obj = this;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const furtherEducation = state.items.find(i => i.id === selectedIds[0]);
        if (!furtherEducation) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRecordFurtherEducationParameter, furtherEducation.furtherEducationCode), false) === ConfirmationDialogResult.Yes) {
            const saveData = this.getRemoveSaveData(this.props, selectedIds);
            // Call server
            const result = await AppUtils.callService(() => recordService.recordEditRemoveFurtherEducations(saveData.obj));
            if (!result) return;
            obj.props.onRemoved(selectedIds);
        }
    };

    static createNewRecordEditItemFurtherEducation = (furtherEducationGroups: IRecordFurtherEducationGroupNode[]): RecordEditItemFurtherEducation => {
        const result = new RecordEditItemFurtherEducation();
        result.id = Base.getGuid();
        result.furtherEducationId = "";
        result.startDate = Base.getNowUtcDate().addDays(-1).getTime();
        result.activeState = 1;
        result.setFurtherEducationCode2(furtherEducationGroups);
        return result;
    };
    
    handleAdd = () => {
        const props = this.props;
        this.setState({
            item: RecordEditFurtherEducationList.createNewRecordEditItemFurtherEducation(props.furtherEducationGroups),
            showEditDialog: true
        });
    };

    handleOpen = (id: string) => {
        if (!id) return;
        const state = this.state;
        const index = state.items.findIndex(i => i.id === id);
        if (index < 0) return;
        this.setState({
            selectedId: id,
            item: state.items[index],
            showEditDialog: true,
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < state.items.length - 1
        });
    };

    onRecordUserFurtherEducationDialogSaved = async (recordFurtherEducation: IRecordEditItemFurtherEducation, closeDialog: boolean) => {
        if (!recordFurtherEducation) return;
        this.setState({
            showEditDialog: !closeDialog,
            selectedId: recordFurtherEducation.id,
            item: recordFurtherEducation
        });
        this.props.onSaved(recordFurtherEducation);
    };

    onRecordUserFurtherEducationDialogCancel = () => {
        this.setState({
            showEditDialog: false
        });
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        if (index < 0 || index >= state.items.length) return;
        this.setState({
            selectedId: state.items[index].id,
            item: state.items[index],
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < state.items.length - 1
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <VerticalContainer
                    before={<>
                        <ActionContainer
                            margin="1rem 0"
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter}
                                    appendElements={[
                                        <FormControlLabel
                                            key="showArchived"
                                            label={Translations.ShowArchived}
                                            control={
                                                <Checkbox
                                                    checked={state.showArchived}
                                                    onChange={(_e, checked) => this.changeShowArchived(checked)}
                                                />
                                            }
                                        />
                                    ]}
                                />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddFurtherEducation + " / " + Translations.Competency}
                                    removeTooltip={Translations.RemoveFurtherEducation + " / " + Translations.Competency}
                                    addDisabled={props.isReadOnly}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                        {!!props.isReadOnly &&
                            <Alert severity="info" sx={{ marginBottom: "1rem" }}>{Translations.NewRecordUserMustBeSavedBeforeFurtherEducationsCanBeAdded}</Alert>
                        }
                    </>}
                    main={<>
                        {!props.isReadOnly &&
                            <List
                                intialized={true}
                                isLoading={state.isLoading}
                                hasMore={false}
                                globalLoadingInUse={false}
                                height="100%"
                                header={{
                                    columns: [
                                        { size: 3, column: "code", content: Translations.Education + " / " + Translations.Competency }, // "col-xl-6 col-3"
                                        { size: 1, column: "scope", content: Translations.Scope }, // 1
                                        { size: 1, column: "scopeUnit", content: Translations.ScopeUnit }, // 1
                                        { size: 2, column: "startDate", content: Translations.TrainingStartDate }, // 2
                                        { size: 2, column: "endDate", content: Translations.TrainingEndDate }, // 2
                                        { size: 2, column: "validityEndDate", content: Translations.ValidityEndDate }, // 2
                                        { size: 1, column: "archived", content: Translations.Archived } // 1
                                    ]
                                }}
                                lines={
                                    state.furtherEducationGroups.flatMap((item) => {
                                        let result: IListValueLineData[] = [];
                                        result.push({
                                            columns: [
                                                {
                                                    size: 3,
                                                    content:
                                                        <Box display="flex" justifyContent="space-between" flexGrow={2}>
                                                            <Typography>{item.code}</Typography>
                                                            <Box display="flex">
                                                                {item.qualification
                                                                    ? <ListStateIconCertificate valid={item.qualification.valid} />
                                                                    : null}
                                                                {item.qualification
                                                                    ? <Typography sx={{ marginLeft: ".5rem" }}>{RecordListItemUserQualification.getValidQualificationNumberStr(item.qualification)}</Typography>
                                                                    : null}
                                                            </Box>
                                                        </Box>
                                                }
                                            ],
                                            id: item.id,
                                            unselectable: true
                                        });
                                        result = result.concat(
                                            item.educations.map((item) => {
                                                return {
                                                    columns: [
                                                        {
                                                            size: 3,
                                                            content:
                                                                <RecordEditListFirstCol
                                                                    id={item.id}
                                                                    paddingLeft=".5rem"
                                                                    code={item.furtherEducationCode}
                                                                    attachments={props.attachments}
                                                                    suffixElement={item.isQualification ? <ListStateIconCertificate valid={item.qualificationValid} /> : null}
                                                                    onClick={this.handleOpen}
                                                                />
                                                        },
                                                        { size: 1, content: item.scope.toLocaleFixed(0) },
                                                        { size: 1, content: item.scopeUnitStr },
                                                        { size: 2, content: Base.utcTimeToDateStr(item.startDate) },
                                                        { size: 2, content: Base.utcTimeToDateStr(item.endDate) },
                                                        { size: 2, content: Base.utcTimeToDateStr(item.validityEndDate) },
                                                        { size: 1, content: item.activeState > 0.5 ? Translations.No : Translations.Yes }
                                                    ],
                                                    id: item.id,
                                                };
                                            })
                                        );
                                        return result;
                                    })
                                }
                                noDataMessage={Translations.AddFurtherEducations}
                                sortColumn={state.sortColumn}
                                sortOrderIsAsc={state.sortOrderIsAsc}
                                selectedId={state.selectedId}
                                checkedIds={state.checkedIds}
                                onSetSelectedIds={this.handleSetSelectedIds}
                                onSearchLines={this.handleSearchLines}
                                onOpenLine={this.handleOpen}
                            />
                        }
                    </>}
                />
                {state.showEditDialog &&
                    <RecordUserFurtherEducationDialog
                        companyId={props.companyId}
                        recordId={props.recordId}
                        userCode={props.userCode}
                        recordQualifications={props.recordQualifications}
                        furtherEducationGroups={props.furtherEducationGroups}
                        item={state.item}
                        attachments={props.attachments}
                        hasPrevious={state.selectedItemHasPrevious}
                        hasNext={state.selectedItemHasNext}
                        onSaved={this.onRecordUserFurtherEducationDialogSaved}
                        onClose={this.onRecordUserFurtherEducationDialogCancel}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                        onAttachmentsRemoved={props.onAttachmentsRemoved}
                        onAttachmentsAdded={props.onAttachmentsAdded}
                    />
                }
            </>
        );
    }
}